import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { api, getAccess } from "@/lib/api";
import { deviceFingerprint } from "@/lib/deviceIdentity";
import { useAuth } from "@/hooks/useAuth";
import { usePlayer } from "@/hooks/usePlayer";
import { PlayerProgressContext } from "@/contexts/PlayerContext";
import { useToast } from "@/contexts/ToastContext";
import type { Track } from "@/types/types";

/** slowson Connect — every open app is a "device" the others can see and
 *  drive (Spotify Connect): play/pause/skip/seek/volume from your phone,
 *  or move what's playing to another device without losing the spot.
 *  Backend relay: api/connect.py (WebSocket + Redis pub/sub). */

export type RemoteState = {
  track: Track | null; playing: boolean; position: number; duration: number;
  volume: number; shuffle: boolean; ts: number;
};
export type ConnectDevice = {
  id: string; name: string; kind: "desktop" | "mobile" | "tablet"; seen: number;
  state: RemoteState | null;
};
type Cmd = "toggle" | "play" | "pause" | "next" | "prev" | "seek" | "volume" | "shuffle" | "handoff";

interface ConnectCtx {
  selfId: string;
  selfName: string;
  connected: boolean;
  devices: ConnectDevice[];
  /** Another device that is currently playing while this one is idle. */
  activeRemote: ConnectDevice | null;
  sendCmd: (to: string, cmd: Cmd, args?: Record<string, unknown>) => void;
  /** Move playback: from this device to `to`, or between two other devices. */
  playOn: (to: string) => void;
  rename: (name: string) => void;
}

const ConnectContext = createContext<ConnectCtx | null>(null);
const NAME_KEY = "sl.deviceName";

function deviceKind(): "desktop" | "mobile" | "tablet" {
  const ua = navigator.userAgent;
  if (/iPad|Tablet/.test(ua) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)) return "tablet";
  if (/Mobi|iPhone|Android/.test(ua)) return "mobile";
  return "desktop";
}

function defaultName(): string {
  const ua = navigator.userAgent;
  if (/iPhone/.test(ua)) return "iPhone";
  if (/iPad/.test(ua) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)) return "iPad";
  if (/Android/.test(ua)) return /Mobi/.test(ua) ? "Téléphone Android" : "Tablette Android";
  const browser = /Edg\//.test(ua) ? "Edge" : /Firefox\//.test(ua) ? "Firefox" : /Chrome\//.test(ua) ? "Chrome" : /Safari\//.test(ua) ? "Safari" : "Navigateur";
  const os = /Windows/.test(ua) ? "PC" : /Macintosh/.test(ua) ? "Mac" : /Linux/.test(ua) ? "Linux" : "Ordinateur";
  return `${os} · ${browser}`;
}

export function ConnectProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const player = usePlayer();
  const toast = useToast();
  const progress = useContext(PlayerProgressContext);
  const selfId = useMemo(() => deviceFingerprint().replace(/[^A-Za-z0-9_-]/g, "").slice(0, 64), []);
  const [selfName, setSelfName] = useState(() => localStorage.getItem(NAME_KEY) || defaultName());
  const [connected, setConnected] = useState(false);
  const [devices, setDevices] = useState<ConnectDevice[]>([]);
  const wsRef = useRef<WebSocket | null>(null);
  const playerRef = useRef(player);
  const progressRef = useRef(progress);
  useEffect(() => { playerRef.current = player; }, [player]);
  useEffect(() => { progressRef.current = progress; }, [progress]);

  const send = useCallback((msg: object) => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify(msg));
  }, []);

  const localState = useCallback((): RemoteState => {
    const p = playerRef.current;
    return {
      track: p.current, playing: p.playing, position: progressRef.current,
      duration: p.duration || (p.current?.duration_ms ?? 0) / 1000, volume: p.volume,
      shuffle: p.shuffle, ts: Date.now(),
    };
  }, []);

  /** Ship this device's queue to `to` and go quiet here. */
  const transferTo = useCallback((to: string) => {
    const p = playerRef.current;
    if (!p.current) return;
    const upcoming = p.upNext.slice(0, 150);
    send({
      kind: "transfer", to,
      payload: { queue: [p.current, ...upcoming], index: 0, position: progressRef.current, playing: true },
    });
    if (p.playing) p.toggle();
  }, [send]);

  const handleMessage = useCallback((msg: Record<string, unknown>) => {
    const p = playerRef.current;
    switch (msg.kind) {
      case "devices":
        setDevices((msg.devices as ConnectDevice[]) || []);
        break;
      case "state":
        setDevices((ds) => ds.map((d) => d.id === msg.from ? { ...d, state: msg.state as RemoteState, seen: Date.now() / 1000 } : d));
        break;
      case "cmd": {
        const args = (msg.args || {}) as Record<string, unknown>;
        switch (msg.cmd) {
          case "toggle": p.toggle(); break;
          case "play": if (!p.playing) p.toggle(); break;
          case "pause": if (p.playing) p.toggle(); break;
          case "next": p.next(); break;
          case "prev": p.prev(); break;
          case "seek": if (typeof args.sec === "number") p.seek(args.sec); break;
          case "volume": if (typeof args.v === "number") p.setVolume(Math.max(0, Math.min(1, args.v))); break;
          case "shuffle": p.cycleShuffle(); break;
          case "handoff": if (typeof args.to === "string") transferTo(args.to); break;
        }
        break;
      }
      case "transfer": {
        const pl = (msg.payload || {}) as { queue?: Track[]; index?: number; position?: number };
        if (!pl.queue?.length) break;
        void p.play(pl.queue, pl.index ?? 0);
        const pos = pl.position ?? 0;
        if (pos > 2) {
          // jump to where the other device was once the stream is playing
          const t0 = Date.now();
          const tick = () => {
            if (progressRef.current > 0.3) { playerRef.current.seek(pos); return; }
            if (Date.now() - t0 < 12000) window.setTimeout(tick, 250);
          };
          window.setTimeout(tick, 400);
        }
        window.setTimeout(() => {
          if (!playerRef.current.playing) toast.show("Appuie sur ▶ pour lancer la musique sur cet appareil");
        }, 4000);
        break;
      }
    }
  }, [toast, transferTo]);
  const handleRef = useRef(handleMessage);
  useEffect(() => { handleRef.current = handleMessage; }, [handleMessage]);

  // connection lifecycle with backoff; a 4401 means the access token aged
  // out — any authenticated call refreshes it, then we reconnect
  useEffect(() => {
    if (!user) return;
    let stopped = false;
    let retry = 0;
    let timer: number | undefined;
    let ping: number | undefined;

    const open = () => {
      const token = getAccess();
      if (!token || stopped) return;
      const proto = location.protocol === "https:" ? "wss" : "ws";
      const q = new URLSearchParams({ token, device: selfId, name: selfName, kind: deviceKind() });
      const ws = new WebSocket(`${proto}://${location.host}/api/connect/ws?${q}`);
      wsRef.current = ws;
      ws.onopen = () => {
        retry = 0;
        setConnected(true);
        if (playerRef.current.current) ws.send(JSON.stringify({ kind: "state", state: localState() }));
        ping = window.setInterval(() => ws.readyState === WebSocket.OPEN && ws.send('{"kind":"ping"}'), 25000);
      };
      ws.onmessage = (e) => {
        try { handleRef.current(JSON.parse(e.data)); } catch { /* ignore */ }
      };
      ws.onclose = async (e) => {
        setConnected(false);
        window.clearInterval(ping);
        if (stopped) return;
        if (e.code === 4401) { await api("/api/auth/me").catch(() => {}); }
        retry = Math.min(retry + 1, 6);
        timer = window.setTimeout(open, 1000 * 2 ** retry);
      };
    };
    open();
    const onVisible = () => {
      if (document.visibilityState === "visible" && wsRef.current?.readyState !== WebSocket.OPEN) {
        window.clearTimeout(timer); retry = 0; open();
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      stopped = true;
      window.clearTimeout(timer);
      window.clearInterval(ping);
      document.removeEventListener("visibilitychange", onVisible);
      wsRef.current?.close();
      wsRef.current = null;
    };
    // selfName changes go through "rename", not a reconnect
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, selfId, localState]);

  // report what this device plays: on every meaningful change, and every
  // 15 s while playing so remotes can keep their progress bar honest
  const p = player;
  useEffect(() => {
    if (!connected || !p.current) return;
    send({ kind: "state", state: localState() });
  }, [connected, p.current?.id, p.playing, p.volume, p.shuffle, send, localState]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!connected || !p.playing) return;
    const t = window.setInterval(() => send({ kind: "state", state: localState() }), 15000);
    return () => window.clearInterval(t);
  }, [connected, p.playing, send, localState]);
  // a seek (big jump) is worth an immediate update
  const lastPosRef = useRef(0);
  useEffect(() => {
    const jump = Math.abs(progress - lastPosRef.current) > 4;
    lastPosRef.current = progress;
    if (jump && connected && p.current) send({ kind: "state", state: localState() });
  }, [progress]); // eslint-disable-line react-hooks/exhaustive-deps

  const sendCmd = useCallback((to: string, cmd: Cmd, args?: Record<string, unknown>) => {
    send({ kind: "cmd", to, cmd, args: args || {} });
  }, [send]);

  const others = useMemo(() => devices.filter((d) => d.id !== selfId), [devices, selfId]);
  const activeRemote = useMemo(() => {
    if (player.playing) return null;
    const now = Date.now();
    const live = others.filter((d) => d.state?.track && now - (d.state.ts || 0) < 6 * 3600 * 1000);
    return live.find((d) => d.state?.playing) ?? null;
  }, [others, player.playing]);

  const playOn = useCallback((to: string) => {
    if (to === selfId) {
      // pull playback here from whichever device is playing
      const src = activeRemote ?? others.find((d) => d.state?.track);
      if (src) sendCmd(src.id, "handoff", { to: selfId });
      return;
    }
    if (playerRef.current.current) { transferTo(to); return; }
    const src = activeRemote ?? others.find((d) => d.id !== to && d.state?.track);
    if (src) sendCmd(src.id, "handoff", { to });
  }, [selfId, activeRemote, others, sendCmd, transferTo]);

  const rename = useCallback((name: string) => {
    const n = name.trim().slice(0, 40);
    if (!n) return;
    setSelfName(n);
    try { localStorage.setItem(NAME_KEY, n); } catch { /* ignore */ }
    send({ kind: "rename", name: n });
  }, [send]);

  const value = useMemo<ConnectCtx>(() => ({
    selfId, selfName, connected, devices, activeRemote, sendCmd, playOn, rename,
  }), [selfId, selfName, connected, devices, activeRemote, sendCmd, playOn, rename]);

  return <ConnectContext.Provider value={value}>{children}</ConnectContext.Provider>;
}

export function useConnect(): ConnectCtx | null {
  return useContext(ConnectContext);
}
